import React from 'react';
import { Box, Container, Typography, useTheme } from '@mui/material';
import { useTranslation } from '../contexts/TranslationContext';
import { getLocalizedString, type LocalizedString } from '../utils/localizedText';

export type PageParagraphSectionConfig = {
  enabled?: boolean;
  text?: LocalizedString;
  align?: 'left' | 'center' | 'right';
  textColor?: string;
  fontSize?: number;
  maxWidth?: 'sm' | 'md' | 'lg';
  variant?: 'plain' | 'card';
  bgColor?: string;
};

const clamp = (n: any, min: number, max: number) => Math.max(min, Math.min(max, Number(n) || 0));

const safeColor = (v: any) => {
  const s = String(v || '').trim();
  if (!s) return '';
  return s.slice(0, 64);
};

const PageParagraphSection: React.FC<{ config?: PageParagraphSectionConfig | null }> = ({ config }) => {
  const cfg: PageParagraphSectionConfig = (config && typeof config === 'object') ? config : {};
  const { language } = useTranslation();
  const theme = useTheme();

  if (cfg.enabled === false) return null;

  const text = getLocalizedString(cfg.text, language, 'de', '');
  if (!text.trim()) return null;

  const align: 'left' | 'center' | 'right' = cfg.align === 'left' ? 'left' : (cfg.align === 'right' ? 'right' : 'center');
  const maxWidth: 'sm' | 'md' | 'lg' = cfg.maxWidth === 'sm' ? 'sm' : (cfg.maxWidth === 'md' ? 'md' : 'lg');
  const fontSize = clamp(cfg.fontSize, 12, 32) || 18;
  const textColor = safeColor(cfg.textColor) || theme.palette.text.primary;
  const variant: 'plain' | 'card' = cfg.variant === 'card' ? 'card' : 'plain';
  const bgColor = safeColor(cfg.bgColor) || (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.75)');

  return (
    <Box sx={{ py: { xs: 6, md: 9 } }}>
      <Container maxWidth={maxWidth}>
        <Box
          sx={variant === 'card' ? {
            borderRadius: { xs: 4, md: 5 },
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: bgColor,
            backdropFilter: 'blur(10px)',
            boxShadow: '0 18px 50px rgba(2,6,23,0.10)',
            px: { xs: 2.25, sm: 3.25 },
            py: { xs: 2.25, sm: 3 },
          } : {}}
        >
          <Typography
            variant="body1"
            sx={{
              textAlign: align,
              fontSize: `${fontSize}px`,
              lineHeight: 1.9,
              color: textColor,
              whiteSpace: 'pre-wrap',
            }}
          >
            {text}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PageParagraphSection;
